import React from 'react';
import { Col, Row } from 'react-bootstrap';
import './Sidebar.scss';
import flatten from 'flat';
import pluralize from 'pluralize';
import getIncludedResource from '../../../../../../helpers/getIncludedResource';
import Broker from '../../../Sidebar/Broker/Broker';
import SchemeSummary from '../../../Sidebar/SchemeSummary/SchemeSummary';
import SidebarSection from '../../../Sidebar/SidebarSection/SidebarSection';
import QuoteActions from '../../../Sidebar/QuoteActions/QuoteActions';
import PremiumAdjust from '../../../Sidebar/PremiumAdjust/PremiumAdjust';
import AdminFeeAdjust from '../../../Sidebar/AdminFeeAdjust/AdminFeeAdjust';
import MtaPremiumSummary from '../../../Sidebar/MtaPremiumSummary/MtaPremiumSummary';
import MtaPremiumOverride from '../../../Sidebar/MtaPremiumOverride/MtaPremiumOverride';

const familyDescription = (groupType, insuredAdults, insuredChildren) => {
  if (!groupType) {
    return '';
  }
  const numAdults = insuredAdults.length;
  const numChildren = insuredChildren ? insuredChildren.length : 0;
  const displayAdults = `${numAdults} ${pluralize('adult', numAdults)}`;
  const displayChildren = numChildren
    ? `, ${numChildren} ${pluralize('child', numChildren)}`
    : '';
  return `${groupType} (${displayAdults}${displayChildren})`;
};

const Sidebar = ({
  productName,
  showSave,
  calculateQuotes,
  showSchemeInfo,
  application,
  source,
  isMta,
  selectedSchemes,
  calculatedPremiums,
  isSaving,
  isCalculating,
  isPremiumAdjusting,
  handleCommissionAdjustments,
  goToNextPage,
  saveAndExit,
  saveEmailAndExit,
  isSubmitting,
  formErrors,
  submitFailed,
}) => {
  const applyAdminFeeOverride = () => {
    return calculateQuotes();
  };

  const hasEmail = !!application.data.attributes.metadata.adults[0].email;
  const validationErrors = Object.values(flatten(formErrors));
  const allPremiums = calculatedPremiums.standard.concat(calculatedPremiums.enhanced);
  const selected =
    allPremiums.length && selectedSchemes && !!selectedSchemes.length
      ? selectedSchemes.reduce((carry, scheme) => {
          const matchedPremium = allPremiums.find(
            (premium) => premium.scheme.id === scheme.id,
          );
          if (matchedPremium) {
            carry.push(matchedPremium);
          }
          return carry;
        }, [])
      : [];

  return (
    <div className="pa-sidebar">
      <div className={'sidebar-sticky-container'}>
        <Row className="sticky-inner-container sticky-sidebar">
          <Col xs={12}>
            <h4 className="quote-summary-title">
              <span>
                Quote Summary {isMta && <span className="mta-logo-sidebar">MTA</span>}
              </span>
            </h4>
          </Col>
          {showSchemeInfo && !selected.length > 0 && (
            <Col xs={12}>
              <div className="col-xs-12 quote-divider">
                <div className="error-box">
                  <span>Please select 1 scheme to proceed</span>
                </div>
              </div>
            </Col>
          )}
          <SidebarSection label={'Product:'} info={productName} />
          <SidebarSection
            label={'Policy number:'}
            info={application.data.attributes.policy_number}
          />
          <SidebarSection
            label={'Cover Selection:'}
            info={familyDescription(
              application.data.attributes.metadata.group_type,
              application.data.attributes.metadata.adults,
              application.data.attributes.metadata.children,
            )}
          />

          {submitFailed && !!validationErrors.length && (
            <SidebarSection
              label={<h4>Attention required</h4>}
              info={
                <div className="error-box">
                  {/* TODO: This is a work around for PayTab.jsx L:55.
                  An object is created and added to the list of errors.
                  causing object/array iteration fatal error. */}
                  {validationErrors.map((error) => {
                    if (typeof error === 'string' || error instanceof String) {
                      return <div className={'error-item'}>{error}</div>;
                    }
                  })}
                </div>
              }
            />
          )}
          <Broker
            broker={getIncludedResource(application.data, application.included, 'broker')}
            source={source}
          />

          {showSchemeInfo &&
            selected.map((selectedPremium) => {
              const mtaPremium = selectedPremium.mta;
              return (
                <>
                  <SchemeSummary
                    key={selectedPremium.scheme.id}
                    isSaving={isSaving}
                    isMta={isMta}
                    selection={selectedPremium}
                  />
                  <MtaPremiumSummary
                    premium={mtaPremium ? mtaPremium.new_premium : null}
                    scheme={selectedPremium.scheme}
                    benefits={selectedPremium.benefits}
                    taxRate={selectedPremium.rates.tax_rate}
                    isMta={isMta}
                  />
                  <MtaPremiumSummary
                    premium={mtaPremium ? mtaPremium.new_premium : null}
                    scheme={selectedPremium}
                    benefits={selectedPremium.benefits}
                    taxRate={selectedPremium.rates.tax_rate}
                    isMta={isMta}
                    payable={selectedPremium}
                  />
                </>
              );
            })}

          {!isSubmitting && selected.length > 0 && (
            <QuoteActions
              showSave={showSave}
              showEmail={hasEmail && showSave}
              saveAndExit={saveAndExit}
              saveEmailAndExit={saveEmailAndExit}
              goToNextPage={goToNextPage}
              isSaving={isSaving}
              showNext={showSave}
            />
          )}
          {selected.length === 1 && showSchemeInfo && selected[0] && (
            <>
              <MtaPremiumOverride
                hasPremiums={!!selectedSchemes.length}
                selectedPremium={selected[0]}
                isMta={isMta}
                handleGrossOverride={applyAdminFeeOverride}
              />
              <PremiumAdjust
                selectedScheme={selected[0]}
                isMta={isMta}
                isCalculating={isCalculating}
                handleCommissionAdjustments={handleCommissionAdjustments}
              />
              <AdminFeeAdjust
                selectedScheme={selected[0]}
                isPremiumAdjusting={isPremiumAdjusting}
                applyAdminFeeOverride={applyAdminFeeOverride}
              />
            </>
          )}
        </Row>
      </div>
    </div>
  );
};
export default Sidebar;
